import { Disposable } from './Disposable';
import { CancellablePromise } from './PromiseHelper';
import React from 'react';

/**
 * A helper class that will trigger various actions when the target React component unmounts.
 *
 * Registration is accepted at any time — there is no guard at either end. Nothing is disposed on
 * an instance React constructs and then abandons without ever mounting it, so do not register
 * from a constructor or field initializer; `componentDidMount` is the earliest safe point. Nor is
 * anything disposed for a registration arriving after unmount: `dispose()` empties the callback
 * list rather than sealing it, so the same helper can serve a remount, which means a late
 * `intervalUntilUnmounted`, or a poll loop's late `onUnmount` abort, would poll forever. Keep every
 * await whose success leads to a registration wrapped in `cancelIfUnmounted`, which turns a
 * resolution after unmount into an `AbortError`. A rejection after unmount keeps its original
 * error, so a `catch` that registers must check for unmount itself. `ConfirmEmailStep` is the
 * worked example.
 */
export default class UnmountHelper<T extends React.Component> {
    private readonly disposer = new Disposable();

    constructor(target: T) {
        // Only `componentWillUnmount` is hooked. `componentDidMount` must stay untouched:
        // `observer` replaces it on the prototype and throws if an instance property shadows
        // it, so the Disposable is created up-front instead of at mount. `dispose()` empties
        // the callback list rather than sealing it, so the same Disposable serves a remount.
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const originalWillUnmount = target.componentWillUnmount;
        target.componentWillUnmount = () => {
            originalWillUnmount?.call(target);
            this.disposer.dispose();
        };
    }

    intervalUntilUnmounted(action: () => unknown, intervalMs: number) {
        this.disposer.intervalIfNotDisposed(action, intervalMs);
    }

    cancelIfUnmounted<T>(prom: CancellablePromise<T> | Promise<T>): Promise<T> {
        return this.disposer.cancelIfDisposed(prom);
    }

    onUnmount(action: () => unknown) {
        this.disposer.onDispose(action);
    }

    listenUntilUnmounted<K extends keyof HTMLElementEventMap, EL extends HTMLElement>(
        el: EL,
        event: K,
        listener: (this: HTMLElement, ev: HTMLElementEventMap[K]) => unknown,
        captureOrOptions: boolean | AddEventListenerOptions | undefined = undefined
    ) {
        el.addEventListener(event, listener, captureOrOptions);
        this.disposer.onDispose(() => el.removeEventListener(event, listener, captureOrOptions));
    }
}
